// client/src/pages/admin/Security.tsx
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Plus, Trash2, Users, ChevronRight, CloudCog, Save, UserPlus, X, RefreshCw, Upload, Search } from 'lucide-react';
import { toast } from 'react-toastify';
import type { SecurityGroup, PermissionKey } from '../../types/permissions';
import { getPermissionsByCategory } from '../../types/permissions';

const ACCENT = '#7aa2ff';

/** One Botfield AI skill or workflow, from the hub's catalog (api/shared/hubCatalog.js). */
type CatalogPermission = {
  key: string;
  label: string;
  description?: string;
  status: string;
  kind: string;
};

/** hub.skill.<id> / hub.workflow.<id>: the shape securityStore.isStorableKey accepts. */
const CATALOG_KEY_RE = /^hub.(skill|workflow).[a-z0-9][a-z0-9-]{1,62}$/;

type AdGroup = {
  groupId: string;
  displayName: string;
  description?: string;
};

// ─── API helpers ──────────────────────────────────────────────────────────────
async function apiFetch(path: string, opts?: RequestInit) {
  const r = await fetch(path, { credentials: 'include', ...opts });
  const data = await r.json();
  if (!r.ok) throw new Error(data?.error || `HTTP ${r.status}`);
  return data;
}

// ─── Permission matrix for a single group ────────────────────────────────────
function PermissionMatrix({
  permissions,
  onChange,
  catalog = [],
  catalogError = null,
}: {
  permissions: string[];
  onChange: (next: string[]) => void;
  catalog?: CatalogPermission[];
  catalogError?: string | null;
}) {
  // The fixed keys by category, then one checkbox per Botfield AI skill or workflow read from the hub's catalog. A
  // catalog key the group holds that the catalog no longer lists (retired, or the read failed) is still drawn, so it
  // can be unchecked rather than kept invisibly.
  const listed = new Set(catalog.map(c => c.key));
  const orphans = permissions.filter(k => CATALOG_KEY_RE.test(k) && !listed.has(k))
    .map(k => ({ key: k, label: k, status: 'unlisted', kind: '', description: 'Not in the Botfield AI catalog right now (retired, or the catalog could not be read). Uncheck to remove it.' }));
  const byCategory: Record<string, { key: string; label: string; description?: string; status?: string }[]> = {
    ...getPermissionsByCategory(),
  };
  if (catalog.length || orphans.length) byCategory['Botfield AI skills & workflows'] = [...catalog, ...orphans];
  const set = new Set(permissions);

  function toggle(key: string) {
    const next = new Set(set);
    if (next.has(key)) next.delete(key); else next.add(key);
    onChange(Array.from(next));
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {catalogError && (
        <div className="muted" style={{ fontSize: 12, padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border)' }}>
          {catalogError}
        </div>
      )}
      {Object.entries(byCategory).map(([category, defs]) => (
        <div key={category}>
          <div className="muted" style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>
            {category}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {defs.map(def => (
              <label
                key={def.key}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '7px 10px',
                  borderRadius: 8,
                  cursor: 'pointer',
                  background: set.has(def.key) ? `${ACCENT}12` : 'transparent',
                  border: `1px solid ${set.has(def.key) ? `${ACCENT}44` : 'var(--border)'}`,
                  transition: 'background .1s, border-color .1s',
                }}
              >
                <input
                  type="checkbox"
                  checked={set.has(def.key)}
                  onChange={() => toggle(def.key)}
                  style={{ accentColor: ACCENT, width: 14, height: 14, flexShrink: 0 }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>
                    {def.label}
                    {def.status && (
                      <span className="muted" style={{ fontSize: 10.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em', marginLeft: 8 }}>
                        {def.status}
                      </span>
                    )}
                  </div>
                  {def.description && (
                    <div className="muted" style={{ fontSize: 11, marginTop: 1 }}>{def.description}</div>
                  )}
                </div>
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Azure AD Group Picker (from uploaded CSV picklist) ──────────────────────
function AzureAdGroupPicker({
  value,
  onChange,
  adGroups,
}: {
  value: string | null;
  onChange: (groupId: string | null, displayName?: string) => void;
  adGroups: AdGroup[];
}) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedGroup = adGroups.find(g => g.groupId === value);

  const filtered = search
    ? adGroups.filter(g => g.displayName.toLowerCase().includes(search.toLowerCase()))
    : adGroups;

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  if (adGroups.length === 0) {
    return (
      <div className="muted" style={{ fontSize: 12 }}>
        No Azure AD groups loaded. Upload a CSV in the panel above to populate the group picklist.
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      {value ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 6, background: `${ACCENT}12`, border: `1px solid ${ACCENT}44` }}>
          <Users size={13} style={{ color: ACCENT, flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13 }}>{selectedGroup?.displayName || value}</div>
            {selectedGroup?.description && (
              <div className="muted" style={{ fontSize: 10 }}>{selectedGroup.description}</div>
            )}
          </div>
          <button className="icon-btn" type="button" onClick={() => { onChange(null); }} title="Clear" style={{ padding: 2 }}>
            <X size={12} />
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, position: 'relative' }}>
          <Search size={13} style={{ position: 'absolute', left: 10, color: 'var(--muted)', pointerEvents: 'none' }} />
          <input
            className="input"
            type="text"
            placeholder="Search AD groups..."
            value={search}
            onChange={e => { setSearch(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            style={{ width: '100%', fontSize: 12, paddingLeft: 30 }}
          />
        </div>
      )}

      {open && !value && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
          marginTop: 4, maxHeight: 240, overflowY: 'auto',
          background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,.3)',
        }}>
          {filtered.length === 0 && (
            <div className="muted" style={{ padding: '10px 12px', fontSize: 12 }}>No groups match</div>
          )}
          {filtered.map(g => (
            <div
              key={g.groupId}
              onClick={() => { onChange(g.groupId, g.displayName); setSearch(''); setOpen(false); }}
              style={{
                padding: '8px 12px', cursor: 'pointer', fontSize: 13,
                borderBottom: '1px solid var(--border)',
                transition: 'background .1s',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,.05)')}
              onMouseLeave={e => (e.currentTarget.style.background = '')}
            >
              <div style={{ fontWeight: 500 }}>{g.displayName}</div>
              {g.description && (
                <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>{g.description}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Member list editor ───────────────────────────────────────────────────────
function MemberEditor({ members, onChange, readOnly, collapsible }: { members: string[]; onChange: (m: string[]) => void; readOnly?: boolean; collapsible?: boolean }) {
  const [input, setInput] = useState('');
  const [expanded, setExpanded] = useState(false);

  function add() {
    const email = input.trim().toLowerCase();
    if (!email || members.includes(email)) { setInput(''); return; }
    onChange([...members, email]);
    setInput('');
  }

  const shouldCollapse = collapsible && members.length > 0 && !expanded;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {!readOnly && (
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            className="input"
            type="email"
            placeholder="email@bmss.com"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && add()}
            style={{ flex: 1, fontSize: 13 }}
          />
          <button className="btn" type="button" onClick={add} style={{ gap: 5, fontSize: 12 }}>
            <UserPlus size={13} /> Add
          </button>
        </div>
      )}
      {members.length === 0 ? (
        <div className="muted" style={{ fontSize: 12 }}>
          {readOnly ? 'No members synced yet — click Sync Now.' : 'No members — add emails above.'}
        </div>
      ) : shouldCollapse ? (
        <button
          className="btn"
          type="button"
          onClick={() => setExpanded(true)}
          style={{ gap: 6, fontSize: 12, justifyContent: 'center', padding: '8px 12px' }}
        >
          <ChevronRight size={12} /> Show {members.length} member{members.length !== 1 ? 's' : ''}
        </button>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {collapsible && (
            <button
              className="btn"
              type="button"
              onClick={() => setExpanded(false)}
              style={{ gap: 6, fontSize: 11, alignSelf: 'flex-end', padding: '3px 8px', marginBottom: 2 }}
            >
              <X size={10} /> Collapse
            </button>
          )}
          {members.map(m => (
            <div key={m} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px', borderRadius: 6, background: 'rgba(255,255,255,.03)', border: '1px solid var(--border)' }}>
              <span style={{ flex: 1, fontSize: 13 }}>{m}</span>
              {!readOnly && (
                <button
                  className="icon-btn"
                  type="button"
                  onClick={() => onChange(members.filter(x => x !== m))}
                  title="Remove"
                  style={{ padding: 2 }}
                >
                  <X size={12} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── AD Groups CSV Upload Panel ──────────────────────────────────────────────
function AdGroupsPanel({ adGroups, onRefresh }: { adGroups: AdGroup[]; onRefresh: () => void }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  async function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const text = await file.text();
      console.log('[Security] CSV upload — length:', text.length, 'first 300:', text.slice(0, 300));
      await apiFetch('/api/ad-groups', {
        method: 'POST',
        headers: { 'content-type': 'text/plain' },
        body: text,
      });
      toast.success('AD groups uploaded successfully');
      onRefresh();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  return (
    <div className="card" style={{ padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 10, borderLeft: `3px solid ${adGroups.length > 0 ? '#51cf66' : 'var(--muted)'}` }}>
      <CloudCog size={14} style={{ color: adGroups.length > 0 ? '#51cf66' : 'var(--muted)', flexShrink: 0 }} />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 12, fontWeight: 500 }}>
          Azure AD Groups {adGroups.length > 0 ? `(${adGroups.length} loaded)` : '— not configured'}
        </div>
        <div className="muted" style={{ fontSize: 11 }}>
          {adGroups.length > 0
            ? 'Groups available for mapping. Upload a new CSV to update the list.'
            : 'Upload a CSV with columns: groupId, displayName (optional: description) to populate the group picklist.'}
        </div>
      </div>
      <input ref={fileRef} type="file" accept=".csv" onChange={handleUpload} style={{ display: 'none' }} />
      <button
        className="btn"
        type="button"
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
        style={{ fontSize: 11, gap: 4 }}
      >
        <Upload size={11} /> {uploading ? 'Uploading...' : 'Upload CSV'}
      </button>
    </div>
  );
}

// ─── Group editor panel ───────────────────────────────────────────────────────
function GroupEditor({
  group,
  onSave,
  onDelete,
  adGroups,
  onGroupUpdated,
  catalog,
  catalogError,
}: {
  group: SecurityGroup;
  onSave: (updated: SecurityGroup) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  adGroups: AdGroup[];
  onGroupUpdated: (group: SecurityGroup) => void;
  catalog: CatalogPermission[];
  catalogError: string | null;
}) {
  const [draft, setDraft]   = useState<SecurityGroup>({ ...group });
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  // Preserve manual members when toggling to azure_ad and back
  const [manualMembers, setManualMembers] = useState<string[]>(group.members);

  // Reset draft when a different group is selected
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setDraft({ ...group }); setManualMembers(group.syncMode !== 'azure_ad' ? group.members : []); }, [group.id]);

  const isAzureSync = draft.syncMode === 'azure_ad';

  function handleSyncModeChange(mode: 'manual' | 'azure_ad') {
    if (mode === 'azure_ad') {
      // Stash current manual members before switching
      setManualMembers(draft.members);
      setDraft(d => ({ ...d, syncMode: mode, members: [] }));
    } else {
      // Restore manual members when switching back
      setDraft(d => ({ ...d, syncMode: mode, azureAdGroupId: d.azureAdGroupId, members: manualMembers }));
    }
  }

  async function handleSave() {
    setSaving(true);
    try {
      await onSave(draft);
      toast.success(`"${draft.name}" saved.`);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  async function handleSync() {
    setSyncing(true);
    try {
      // Save first to persist any azureAdGroupId changes
      await onSave(draft);
      const result = await apiFetch(`/api/security-group-sync?id=${draft.id}`, { method: 'POST' });
      const syncInfo = result.syncResult;
      toast.success(`Synced ${syncInfo.newCount} member(s)`);
      // Update draft with synced members
      const updated = { ...draft, members: result.members, updatedAt: result.updatedAt };
      setDraft(updated);
      onGroupUpdated(result);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : String(e));
    } finally {
      setSyncing(false);
    }
  }

  async function handleDelete() {
    if (!window.confirm(`Delete group "${group.name}"? This cannot be undone.`)) return;
    try {
      await onDelete(group.id);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Group name + description */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div>
          <label className="muted" style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em', display: 'block', marginBottom: 5 }}>Group Name</label>
          <input
            className="input"
            type="text"
            value={draft.name}
            disabled={draft.isDefault}
            onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
            style={{ width: '100%', fontSize: 14, fontWeight: 600 }}
          />
        </div>
        <div>
          <label className="muted" style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em', display: 'block', marginBottom: 5 }}>Description</label>
          <input
            className="input"
            type="text"
            value={draft.description || ''}
            onChange={e => setDraft(d => ({ ...d, description: e.target.value }))}
            style={{ width: '100%', fontSize: 13 }}
          />
        </div>
      </div>

      {/* Sync mode */}
      {!draft.isDefault && (
        <div className="card" style={{ padding: '12px 14px', overflow: 'visible' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <CloudCog size={14} style={{ color: 'var(--muted)', flexShrink: 0 }} />
            <span style={{ fontSize: 13, fontWeight: 500, flex: 1 }}>Sync Mode</span>
            <select
              value={draft.syncMode}
              onChange={e => handleSyncModeChange(e.target.value as 'manual' | 'azure_ad')}
              style={{ fontSize: 12, background: 'var(--bg)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 6, padding: '3px 8px' }}
            >
              <option value="manual">Manual</option>
              <option value="azure_ad">Azure AD Group</option>
            </select>
          </div>

          {isAzureSync && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, position: 'relative' }}>
              <div style={{ position: 'relative', zIndex: 10 }}>
                <label className="muted" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>Linked Azure AD Group</label>
                <AzureAdGroupPicker
                  value={draft.azureAdGroupId}
                  onChange={(groupId, displayName) => setDraft(d => ({
                    ...d,
                    azureAdGroupId: groupId,
                    description: displayName ? `Synced from Azure AD: ${displayName}` : d.description,
                  }))}
                  adGroups={adGroups}
                />
              </div>
              {draft.azureAdGroupId && (
                <button
                  className="btn"
                  type="button"
                  onClick={handleSync}
                  disabled={syncing}
                  style={{ gap: 6, fontSize: 12, alignSelf: 'flex-start' }}
                >
                  <RefreshCw size={12} />
                  {syncing ? 'Syncing...' : 'Sync Now'}
                </button>
              )}
            </div>
          )}

          {!isAzureSync && (
            <div className="muted" style={{ fontSize: 11 }}>
              Members managed manually below. Switch to Azure AD to sync membership from an Entra group.
            </div>
          )}
        </div>
      )}

      {/* Members — manual mode: always show; azure_ad mode: only after group is selected and synced */}
      {!draft.isDefault && !isAzureSync && (
        <div>
          <label className="muted" style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em', display: 'block', marginBottom: 8 }}>
            Members ({draft.members.length})
          </label>
          <MemberEditor
            members={draft.members}
            onChange={members => setDraft(d => ({ ...d, members }))}
          />
        </div>
      )}
      {!draft.isDefault && isAzureSync && draft.azureAdGroupId && draft.members.length > 0 && (
        <div>
          <label className="muted" style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em', display: 'block', marginBottom: 8 }}>
            Members ({draft.members.length})
            <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}> — synced from Azure AD</span>
          </label>
          <MemberEditor
            members={draft.members}
            onChange={() => {}}
            readOnly
            collapsible
          />
        </div>
      )}
      {draft.isDefault && (
        <div className="muted" style={{ fontSize: 12, fontStyle: 'italic' }}>
          This is the default group — it applies to all authenticated users automatically. Members cannot be added.
        </div>
      )}

      {/* Permissions matrix */}
      <div>
        <label className="muted" style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em', display: 'block', marginBottom: 8 }}>
          Permissions
        </label>
        <PermissionMatrix
          permissions={draft.permissions as string[]}
          onChange={permissions => setDraft(d => ({ ...d, permissions: permissions as PermissionKey[] }))}
          catalog={catalog}
          catalogError={catalogError}
        />
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 10, paddingTop: 4 }}>
        <button
          className="btn primary"
          type="button"
          disabled={saving}
          onClick={handleSave}
          style={{ gap: 6, fontSize: 13 }}
        >
          <Save size={14} />
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
        {!group.isDefault && (
          <button
            className="btn"
            type="button"
            onClick={handleDelete}
            style={{ gap: 6, fontSize: 13, color: 'var(--bad)', borderColor: 'rgba(255,107,107,.3)', marginLeft: 'auto' }}
          >
            <Trash2 size={13} /> Delete Group
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function Security() {
  const [groups, setGroups]           = useState<SecurityGroup[]>([]);
  const [loading, setLoading]         = useState(true);
  const [selectedId, setSelectedId]   = useState<string | null>(null);
  const [creating, setCreating]       = useState(false);
  const [newName, setNewName]         = useState('');
  const newNameRef = useRef<HTMLInputElement>(null);
  const [adGroups, setAdGroups]       = useState<AdGroup[]>([]);
  const [catalog, setCatalog]         = useState<CatalogPermission[]>([]);
  const [catalogError, setCatalogError] = useState<string | null>(null);

  // Load groups
  useEffect(() => {
    apiFetch('/api/security-groups')
      .then(data => {
        setGroups(data.groups);
        setCatalog(data.catalogPermissions || []);
        setCatalogError(data.catalogError || null);
        if (data.groups.length > 0) setSelectedId(data.groups[0].id);
        setLoading(false);
      })
      .catch(e => {
        toast.error('Failed to load groups: ' + e.message);
        setLoading(false);
      });
  }, []);

  // Load AD groups picklist
  const refreshAdGroups = useCallback(() => {
    apiFetch('/api/ad-groups')
      .then(data => setAdGroups(data.groups || []))
      .catch(() => setAdGroups([]));
  }, []);

  useEffect(() => { refreshAdGroups(); }, [refreshAdGroups]);

  useEffect(() => {
    if (creating) setTimeout(() => newNameRef.current?.focus(), 50);
  }, [creating]);

  const selected = groups.find(g => g.id === selectedId) ?? null;

  async function handleSave(updated: SecurityGroup) {
    const result = await apiFetch(`/api/security-group?id=${updated.id}`, {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(updated),
    }) as SecurityGroup;
    setGroups(gs => gs.map(g => g.id === result.id ? result : g));
  }

  async function handleDelete(id: string) {
    await apiFetch(`/api/security-group?id=${id}`, { method: 'DELETE' });
    setGroups(gs => gs.filter(g => g.id !== id));
    setSelectedId(groups.find(g => g.id !== id)?.id ?? null);
    toast.success('Group deleted.');
  }

  async function handleCreate() {
    const name = newName.trim();
    if (!name) return;
    const everyonePermissions = groups.find(g => g.isDefault)?.permissions ?? [];
    const result = await apiFetch('/api/security-groups', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ name, permissions: everyonePermissions }),
    }) as SecurityGroup;
    setGroups(gs => [...gs, result]);
    setSelectedId(result.id);
    setCreating(false);
    setNewName('');
    toast.success(`Group "${name}" created.`);
  }

  function handleGroupUpdated(updated: SecurityGroup) {
    setGroups(gs => gs.map(g => g.id === updated.id ? updated : g));
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Header */}
      <div>
        <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-.01em' }}>Security</div>
        <div className="muted" style={{ fontSize: 13, marginTop: 3 }}>
          Manage groups and control what each group can see and do in the app
        </div>
      </div>

      {/* AD Groups picklist upload */}
      <AdGroupsPanel adGroups={adGroups} onRefresh={refreshAdGroups} />

      {loading ? (
        <div className="muted" style={{ fontSize: 13 }}>Loading...</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: 16, alignItems: 'start' }}>

          {/* Left: group list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {groups.map(g => (
              <div
                key={g.id}
                className="card"
                onClick={() => setSelectedId(g.id)}
                style={{
                  padding: '12px 14px',
                  cursor: 'pointer',
                  borderLeft: `3px solid ${selectedId === g.id ? ACCENT : 'transparent'}`,
                  background: selectedId === g.id ? `${ACCENT}10` : undefined,
                  transition: 'background .1s, border-color .1s',
                }}
                onMouseEnter={e => { if (selectedId !== g.id) e.currentTarget.style.background = 'rgba(255,255,255,.03)'; }}
                onMouseLeave={e => { if (selectedId !== g.id) e.currentTarget.style.background = ''; }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <Users size={13} style={{ color: selectedId === g.id ? ACCENT : 'var(--muted)', flexShrink: 0 }} />
                  <span style={{ fontSize: 13, fontWeight: selectedId === g.id ? 600 : 400, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {g.name}
                  </span>
                  {g.syncMode === 'azure_ad' && (
                    <CloudCog size={11} style={{ color: 'var(--muted)', flexShrink: 0 }} />
                  )}
                  {selectedId === g.id && <ChevronRight size={13} style={{ color: ACCENT, flexShrink: 0 }} />}
                </div>
                {g.isDefault && (
                  <div className="muted" style={{ fontSize: 10, marginTop: 3, marginLeft: 20 }}>Everyone</div>
                )}
                {!g.isDefault && (
                  <div className="muted" style={{ fontSize: 10, marginTop: 3, marginLeft: 20 }}>
                    {g.members.length} member{g.members.length !== 1 ? 's' : ''}
                    {g.syncMode === 'azure_ad' && ' · Azure AD'}
                  </div>
                )}
              </div>
            ))}

            {/* New group */}
            {creating ? (
              <div className="card" style={{ padding: '10px 12px', display: 'flex', gap: 6, alignItems: 'center' }}>
                <input
                  ref={newNameRef}
                  className="input"
                  type="text"
                  placeholder="Group name"
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleCreate(); if (e.key === 'Escape') { setCreating(false); setNewName(''); } }}
                  style={{ flex: 1, fontSize: 12 }}
                />
                <button className="btn primary" type="button" onClick={handleCreate} style={{ fontSize: 11, padding: '4px 8px' }}>Add</button>
                <button className="icon-btn" type="button" onClick={() => { setCreating(false); setNewName(''); }}><X size={13} /></button>
              </div>
            ) : (
              <button
                className="btn"
                type="button"
                onClick={() => setCreating(true)}
                style={{ gap: 6, fontSize: 12, justifyContent: 'center' }}
              >
                <Plus size={13} /> New Group
              </button>
            )}
          </div>

          {/* Right: editor */}
          <div className="card" style={{ padding: '20px 22px' }}>
            {selected ? (
              <GroupEditor
                key={selected.id}
                group={selected}
                onSave={handleSave}
                onDelete={handleDelete}
                adGroups={adGroups}
                onGroupUpdated={handleGroupUpdated}
                catalog={catalog}
                catalogError={catalogError}
              />
            ) : (
              <div className="muted" style={{ fontSize: 13 }}>Select a group to edit.</div>
            )}
          </div>

        </div>
      )}
    </div>
  );
}
